/**
 * Crew & attendee context for XAES Analyst — resolves the same effective
 * crew the Meet Crew modal shows (authored roster → live program roster,
 * with facilitator overrides) and serializes it for every-turn grounding
 * and @-mention transclusion.
 */

import { loadUsers, type UserProfile } from "@/content/users";
import { WORKSHOPS, type Workshop } from "@/content/workshops";
import { ROLE_OBSERVER } from "@/features/auth/roles";
import {
  crewByWorkshop,
  crewFooterByWorkshop,
  type CrewMember,
} from "@/features/workshop/crew-content";
import { resolveCrew } from "@/features/workshop/overrides/workshop-override-content";
import { getEffectiveWorkshopOverride } from "@/stores/useEffectiveWorkshopOverride";
import type { ChatScope } from "./context-builder";

/** Live roster snapshot — set from `XaesAnalyst` when auth.users updates. */
let rosterSnapshot: UserProfile[] | null = null;

/** Update the program roster used for crew resolution (ZITADEL or seed). */
export function setProgramRoster(users: UserProfile[]): void {
  rosterSnapshot = users.length > 0 ? users : null;
}

/** Program roster: snapshot when set, otherwise the seed roster from IDB. */
export function getProgramRoster(): UserProfile[] {
  return rosterSnapshot ?? loadUsers();
}

function isObserver(member: CrewMember): boolean {
  return member.role.toUpperCase().includes("OBSERVER") || member.role === ROLE_OBSERVER;
}

/** Effective crew for one workshop — mirrors MeetCrewModal / CrewPanel. */
export function resolveWorkshopCrew(
  workshopId: Workshop["id"],
  roster?: UserProfile[],
): CrewMember[] {
  const users = roster ?? getProgramRoster();
  const override = getEffectiveWorkshopOverride(workshopId);
  return resolveCrew(override, crewByWorkshop[workshopId] ?? [], users);
}

function formatCrewMemberLines(member: CrewMember): string[] {
  const kind = isObserver(member) ? "observer" : "active attendee";
  const lines: string[] = [
    `- **${member.name}** (\`${member.id}\`) — ${member.role}` +
      `${member.badge ? ` · ${member.badge}` : ""} · _${kind}_`,
  ];
  if (member.bio) lines.push(`  - Bio: ${member.bio}`);
  if (member.agent) {
    lines.push(
      `  - Paired agent: **${member.agent.name}** (${member.agent.type}) — ${member.agent.description}`,
    );
  }
  return lines;
}

/** Compact crew markdown for @-mentions and the workshop scope block. */
export function formatWorkshopCrew(
  workshopId: Workshop["id"],
  roster?: UserProfile[],
): string {
  const crew = resolveWorkshopCrew(workshopId, roster);
  if (crew.length === 0) {
    return "_No crew members resolved for this workshop._";
  }
  const lines: string[] = [`### ${workshopId}`, ""];
  for (const member of crew) {
    lines.push(...formatCrewMemberLines(member));
  }
  const footer = crewFooterByWorkshop[workshopId];
  if (footer) lines.push("", `_Session footer: ${footer}_`);
  return lines.join("\n");
}

function crewWorkshopIds(scope: ChatScope): Workshop["id"][] {
  if (scope === "MC") return WORKSHOPS.map((w) => w.id);
  const ws = WORKSHOPS.find((w) => w.id === scope);
  if (!ws) return [];
  // Active workshop plus priors for continuity (same people often carry forward).
  return WORKSHOPS.filter((w) => w.num <= ws.num).map((w) => w.id);
}

/**
 * Every-turn crew dump — injected via `buildWorkshopContentContext` so the
 * model can discuss crew, attendees, and observers without @-mentions.
 */
export function buildCrewContext(scope: ChatScope, roster?: UserProfile[]): string {
  const ids = crewWorkshopIds(scope);
  const rows: string[] = [];
  for (const wsId of ids) {
    rows.push(formatWorkshopCrew(wsId, roster), "");
  }
  if (rows.length === 0) return "";

  const heading =
    scope === "MC"
      ? "Crew & attendees (all workshops)"
      : "Crew & attendees (active + prior workshops)";

  return [
    `## ${heading}`,
    ``,
    `The roster below is **first-party crew content** — workshop crew cards,` +
      ` the live program roster (facilitators, analysts, observers, directors,` +
      ` engineers), and any facilitator overrides that pin who is shown. **Observers**` +
      ` attend but are typically non-contributing; other roles are **active attendees**.` +
      ` Cite by name and role.`,
    ``,
    `<retrieved source="crew">`,
    rows.join("\n").trim(),
    `</retrieved>`,
  ].join("\n");
}
